import { SentryManualInstance } from "@configs/sentry/manual-sentry";

const DESKTOP_BLOCKER_DEV_KEY = "DESKTOP_BLOCKER_DEV";
const MOBILE_PLATFORM = ["linux armv81", "linux aarch64", "linux arm64", "iphone", "ipad"];

export type DesktopBlockerDetectorResult = {
  isMobileBrowser: boolean;
  blockReason: string;
  userAgent: string;
  userAgentRaw: string;
  criteria: {
    matchMobileUA: boolean;
    platform: string;
    hasTouchSupport: boolean;
  };
};

export type DesktopBlockerInitResult = {
  shouldBlock: boolean;
  userAgent: string;
  context: DesktopBlockerDetectorResult;
  triggerSentryEvent?: () => string | null | undefined;
};

export const runDesktopBlockerDetector = (): DesktopBlockerDetectorResult => {
  let blockReason = "";
  const criteria = {
    matchMobileUA: false,
    platform: "",
    hasTouchSupport: false,
  };

  if (typeof navigator === "undefined") {
    criteria.matchMobileUA = false;
    criteria.platform = "navigator is undefined";
    criteria.hasTouchSupport = false;
    return {
      isMobileBrowser: false,
      blockReason: "Navigator is not found, this request is not from a browser",
      userAgent: "",
      userAgentRaw: "",
      criteria,
    };
  }

  const userAgentRaw = navigator.userAgent;
  const userAgent = userAgentRaw.toLowerCase();
  let isMobileBrowser =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|vivobrowser|samsungbrowser|edg|ucbrowser|miuibrowser|opera mini/i.test(
      userAgent,
    );

  if (!isMobileBrowser) {
    criteria.matchMobileUA = false;
    blockReason = "UA does not match mobile browser pattern";
  }
  let platform = navigator.platform;
  if (platform) {
    platform = platform.toLowerCase();
    criteria.platform = platform;

    if (platform.includes("win")) {
      isMobileBrowser = false;
      blockReason = "Platform is Windows Desktop";
    }

    if (platform.includes("mac")) {
      const hasTouchSupport = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      isMobileBrowser = hasTouchSupport;
      criteria.hasTouchSupport = hasTouchSupport;
      blockReason = "Platform is Mac Desktop";
    }

    // override blocker for
    if (MOBILE_PLATFORM.includes(platform)) {
      isMobileBrowser = true;
    }
  }

  return {
    isMobileBrowser,
    blockReason,
    userAgent,
    userAgentRaw,
    criteria,
  };
};

/**
 * Check if device is on desktop
 */
export const initDesktopBlocker = (sentryClient?: SentryManualInstance) => {
  const result: DesktopBlockerInitResult = {
    shouldBlock: false,
    context: {} as DesktopBlockerDetectorResult,
    userAgent: "",
    triggerSentryEvent: undefined,
  };

  // Check if any bypass flag is set
  const searchParams = new URLSearchParams(window.location.search);
  const searchParamDesktopBlocker = searchParams.get("desktop-blocker");
  if (searchParamDesktopBlocker === "false") {
    localStorage.setItem(DESKTOP_BLOCKER_DEV_KEY, "false");
    return result; // bypass desktop blocker
  } else if (searchParamDesktopBlocker === "true") {
    localStorage.removeItem(DESKTOP_BLOCKER_DEV_KEY);
  }
  const localStorageDesktopBlocker = localStorage.getItem(DESKTOP_BLOCKER_DEV_KEY);
  if (localStorageDesktopBlocker === "false") {
    return result; // bypass desktop blocker
  }

  // Run desktop blocker detector
  const detectorResult = runDesktopBlockerDetector();
  if (!detectorResult.isMobileBrowser) {
    result.shouldBlock = true;
    result.context = detectorResult;
    result.userAgent = detectorResult.userAgentRaw;
  }

  // Capture event with sentry
  if (result.shouldBlock && !!sentryClient) {
    result.triggerSentryEvent = () => {
      const sentryEventAdditionalData: Record<string, any> = {
        blockerEnabled: false,
        blockReason: result.context.blockReason,
        context: {
          ...result.context.criteria,
          userAgent: result.context.userAgent,
          screenSize: `${window.innerWidth}x${window.innerHeight}`,
        },
      };
      const traceId = sentryClient?.captureMessage("Desktop blocker", "warning", sentryEventAdditionalData);
      return traceId;
    };
  }

  return result;
};
